/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import ReactMarkdown from 'react-markdown';

import type { IconProps } from '@coursera/cds-common';
import { Link, Typography2 } from '@coursera/cds-core';
import {
  AchievementIcon,
  AnswerCorrectIcon,
  AssignmentIcon,
  BellIcon,
  CertificateIcon,
  CurriculumIcon,
  DegreeIcon,
  DialogueIcon,
  DiscussionPromptIcon,
  GlobeIcon,
  GradingIcon,
  HelpIcon,
  HomeIcon,
  HonorsAssignmentIcon,
  InstitutionIcon,
  NoteIcon,
  ProfileIcon,
  ProgrammingAssignmentIcon,
  QuizIcon,
  ReadingIcon,
  SlideshowIcon,
  StarIcon,
  VideoIcon,
  WebIcon,
} from '@coursera/cds-icons';

const styles = {
  container: css`
    border-radius: var(--cds-border-radius-100);
    background: var(--cds-color-emphasis-primary-background-xweak);
    padding: var(--cds-spacing-200) var(--cds-spacing-300);
  `,
  gridDisplay: css`
    width: 100%;
    display: grid;
    grid-template-columns: min-content 1fr;
    gap: var(--cds-spacing-100);
  `,
  centerVertically: css`
    display: flex;
    align-items: center;
  `,
  iconContainer: css`
    display: flex;
    align-items: center;
    justify-content: center;

    svg path {
      fill: var(--cds-color-emphasis-primary-content-default);
    }
  `,
};

type CdsIconFromStringProps = {
  name: string;
} & IconProps;

const iconMap: Record<string, React.ComponentType<IconProps>> = {
  AchievementIcon,
  AnswerCorrectIcon,
  AssignmentIcon,
  BellIcon,
  CertificateIcon,
  CurriculumIcon,
  DegreeIcon,
  DialogueIcon,
  DiscussionPromptIcon,
  GlobeIcon,
  GradingIcon,
  HelpIcon,
  HomeIcon,
  HonorsAssignmentIcon,
  InstitutionIcon,
  NoteIcon,
  ProfileIcon,
  ProgrammingAssignmentIcon,
  QuizIcon,
  ReadingIcon,
  SlideshowIcon,
  StarIcon,
  VideoIcon,
  WebIcon,
};

const CdsIconFromString = ({ name, ...iconProps }: CdsIconFromStringProps) => {
  const IconComponent = iconMap[name];

  if (!IconComponent) {
    return null;
  }

  return <IconComponent {...iconProps} />;
};

type LearnerStatsCardProps = {
  headlineStat?: string;
  bodyText?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  cdsIconName?: string;
};

const LearnerStatsCard = ({ bodyText, ctaLabel, ctaUrl, headlineStat, cdsIconName }: LearnerStatsCardProps) => {
  const hasLeadingStatOrIcon = Boolean(headlineStat) || Boolean(cdsIconName);

  return (
    <div css={styles.container}>
      <div css={hasLeadingStatOrIcon && styles.gridDisplay}>
        {cdsIconName ? (
          <div css={styles.iconContainer}>
            <CdsIconFromString name={cdsIconName} size="large" />
          </div>
        ) : (
          <Typography2 component="p" variant="subtitleLarge" css={styles.centerVertically} color="emphasis.primary">
            {headlineStat}
          </Typography2>
        )}
        <Typography2 component="div" variant="bodySecondary" css={styles.centerVertically} color="emphasis.primary">
          <ReactMarkdown
            source={bodyText}
            renderers={{
              paragraph: ({ children }: { children: React.ReactNode }) => <span>{children}</span>,
            }}
          />
        </Typography2>
      </div>
      {ctaLabel && ctaUrl && (
        <Link href={ctaUrl} variant="standard" typographyVariant="bodySecondary" iconPosition="after">
          {ctaLabel}
        </Link>
      )}
    </div>
  );
};

export default LearnerStatsCard;
